import React, { useMemo, useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    TextField,
    MenuItem,
    IconButton
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import axios from "axios";
import {useAuth} from "../components/TokenHandler";

const serviceOptions = ["RPM Passive", "RPM Active", "CCM", "BHI"];

const DATE_TYPES = [
    { value: "start", label: "Start date" },
    { value: "end", label: "End date" },
    { value: "on_hold", label: "On-hold date" },
];

const ProcessSelectedModal = ({
                                  open,
                                  onClose,
                                  selectedFacilities,
                                  onUpdateFacilities
                              }) => {

    const {
        keycloak: kc,
        authenticated,
        infoMessage,
        setInfoMessage,
        givenName,
        email,
        roles,
    } = useAuth();

    const [localSelectedFacilities, setLocalSelectedFacilities] = useState([]);
    console.log("selectedFacilities", selectedFacilities);
    const [formData, setFormData] = useState({
        service_name: "",
        date_type: "start",
        date_value: dayjs(),
        creator: "",
        rep: "",
        notes: ""
    });

    useEffect(() => {
        if (open) {
            setLocalSelectedFacilities(selectedFacilities || []);
            setFormData((p) => ({ ...p, date_value: dayjs() }));
        }
    }, [selectedFacilities, open]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // ----- Helpers -----
    const latestInstance = (facility, serviceName) => {
        const instances = (facility.services || []).filter(s => s.service_name === serviceName);
        if (!instances.length) return null;
        return instances
            .slice()
            .sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0))[0];
    };

    const latestCommittedInstance = (facility, serviceName) => {
        const instances = (facility.services || [])
            .filter(s => s.service_name === serviceName && !!s.id);
        if (!instances.length) return null;
        return instances
            .slice()
            .sort((a, b) => new Date(b.start_date || 0) - new Date(a.start_date || 0))[0];
    };

    // Business rules (use committed snapshot)
    const canAddStartDate = (facility, serviceName) => {
        const latest = latestCommittedInstance(facility, serviceName);
        if (!latest) return true; // no entry yet → can start
        const hasStart = !!latest.start_date;
        const hasEnd = !!latest.stop_date;
        const hasHold = !!latest.on_hold_date;
        // allowed when ended OR on-hold (no end)
        return (hasStart && hasEnd) || (hasStart && !hasEnd && hasHold);
    };

    const canAddEndDate = (facility, serviceName) => {
        const latest = latestCommittedInstance(facility, serviceName);
        if (!latest) return false;
        const hasStart = !!latest.start_date;
        const hasEnd = !!latest.stop_date;
        return hasStart && !hasEnd;
    };

    const canAddOnHoldDate = (facility, serviceName) => {
        const latest = latestCommittedInstance(facility, serviceName);
        if (!latest) return false;
        const hasStart = !!latest.start_date;
        const hasEnd = !!latest.stop_date;
        const hasHold = !!latest.on_hold_date;
        return hasStart && !hasEnd && !hasHold;
    };

    // Stage a change for all selected facilities (preview only)
    const stageChangeForAll = () => {
        const { service_name, date_type, date_value, creator, rep, notes } = formData;
        if (!service_name || !date_value || !creator || !notes.trim()) {
            alert("Please complete Service, Date, Creator, and Notes.");
            return;
        }

        const isoDate = date_value.toISOString();
        const nowISO = new Date().toISOString();

        let anyStaged = false;

        const nextFacilities = localSelectedFacilities.map((facility) => {
            const serviceName = service_name;
            const facUID = facility.facility_unique_id;
            const facName = facility.facility_name;
            const facId = facility.fac_id ?? facility.facility_id ?? null;
            const mondayItemid = facility.monday_itemid ?? null;

            const latestCommitted = latestCommittedInstance(facility, serviceName);

            if (date_type === "start") {
                if (!canAddStartDate(facility, serviceName)) return facility;

                // If latest committed is ON-HOLD (start & on_hold, no end), "unhold" it:
                if (latestCommitted && latestCommitted.start_date && latestCommitted.on_hold_date && !latestCommitted.stop_date) {
                    const updatedService = {
                        ...latestCommitted,
                        // keep a backup so we can undo
                        _prevOnHoldDate: latestCommitted.on_hold_date,
                        _prevStartDate: latestCommitted.start_date,
                        on_hold_date: null,             // clear hold
                        start_date: isoDate,            // restart from chosen date
                        updated_on: nowISO,
                        notes: { note: [...(latestCommitted?.notes?.note || []), { date: nowISO, text: notes, creator }] },
                        _stagedUnhold: true,            // mark staged unhold
                    };
                    anyStaged = true;
                    const updatedServices = (facility.services || []).map(s =>
                        s === latestCommitted ? updatedService : s
                    );
                    return { ...facility, services: updatedServices };
                }

                // Else: ended or no entry → create a new row
                const newService = {
                    id: "",
                    facility_unique_id: facUID,
                    facility_name: facName,
                    fac_id: facId,
                    service_name: serviceName,
                    start_date: isoDate,
                    stop_date: null,
                    on_hold_date: null,
                    rep,
                    notes: { note: [{ date: nowISO, text: notes, creator }] },
                    original_creator: creator,
                    created_on: nowISO,
                    updated_on: nowISO,
                    itemid: mondayItemid,
                    _stagedNew: true,
                };

                anyStaged = true;
                return { ...facility, services: [...(facility.services || []), newService] };
            }

            if (date_type === "end") {
                if (!canAddEndDate(facility, serviceName)) return facility;
                if (!latestCommitted?.id) return facility;

                const updatedService = {
                    ...latestCommitted,
                    stop_date: isoDate,
                    updated_on: nowISO,
                    notes: { note: [...(latestCommitted?.notes?.note || []), { date: nowISO, text: notes, creator }] },
                    _stagedEnd: true,
                };

                anyStaged = true;
                const updatedServices = (facility.services || []).map(s =>
                    s === latestCommitted ? updatedService : s
                );
                return { ...facility, services: updatedServices };
            }

            if (date_type === "on_hold") {
                if (!canAddOnHoldDate(facility, serviceName)) return facility;
                if (!latestCommitted?.id) return facility;

                const updatedService = {
                    ...latestCommitted,
                    on_hold_date: isoDate,
                    updated_on: nowISO,
                    notes: { note: [...(latestCommitted?.notes?.note || []), { date: nowISO, text: notes, creator }] },
                    _stagedHold: true,
                };

                anyStaged = true;
                const updatedServices = (facility.services || []).map(s =>
                    s === latestCommitted ? updatedService : s
                );
                return { ...facility, services: updatedServices };
            }

            return facility;
        });

        if (!anyStaged) {
            alert("No facilities qualified for this change based on your rules.");
            return;
        }

        setLocalSelectedFacilities(nextFacilities);
    };

    // Remove a staged change
    const unstageChange = (facilityId, serviceName, type /* 'start'|'end'|'on_hold'|'unhold' */) => {
        setLocalSelectedFacilities(prev => prev.map(fac => {
            if (fac.facility_unique_id !== facilityId) return fac;
            const services = (fac.services || []).slice();

            if (type === 'start') {
                // remove staged-new row
                const idx = services.findIndex(s => s.service_name === serviceName && !s.id && s._stagedNew);
                if (idx !== -1) {
                    services.splice(idx, 1);
                    return { ...fac, services };
                }
                return fac;
            }

            // revert staged updates on committed row
            const idx = services.findIndex(s =>
                s.service_name === serviceName &&
                !!s.id &&
                ((type === 'end' && s._stagedEnd) ||
                    (type === 'on_hold' && s._stagedHold) ||
                    (type === 'unhold' && s._stagedUnhold))
            );
            if (idx !== -1) {
                const orig = { ...services[idx] };

                if (type === 'end') {
                    orig.stop_date = null;
                    delete orig._stagedEnd;
                } else if (type === 'on_hold') {
                    orig.on_hold_date = null;
                    delete orig._stagedHold;
                } else if (type === 'unhold') {
                    // put back previous values
                    orig.on_hold_date = orig._prevOnHoldDate ?? orig.on_hold_date ?? null;
                    orig.start_date   = orig._prevStartDate   ?? orig.start_date;
                    delete orig._prevOnHoldDate;
                    delete orig._prevStartDate;
                    delete orig._stagedUnhold;
                }

                // remove the last appended note (simple heuristic)
                if (orig.notes?.note?.length) {
                    orig.notes = { note: orig.notes.note.slice(0, -1) };
                }
                services[idx] = orig;
                return { ...fac, services };
            }

            return fac;
        }));
    };

    const handleRemoveFacility = (facilityId) => {
        setLocalSelectedFacilities((prev) => prev.filter((f) => f.facility_unique_id !== facilityId));
    };

    const uniqueServiceNames = useMemo(() => serviceOptions.slice(), []);

    // Cell renderer (+ tiny X for staged items)
    const renderCell = (facility, serviceName) => {
        const inst = latestInstance(facility, serviceName);
        if (!inst) return null;

        // staged unhold (on-hold -> start)
        if (inst._stagedUnhold) {
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        start: {dayjs(inst.start_date).format("YYYY-MM-DD")}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => unstageChange(facility.facility_unique_id, serviceName, 'unhold')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        // staged new start
        if (inst._stagedNew && inst.start_date && !inst.id) {
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        start: {dayjs(inst.start_date).format("YYYY-MM-DD")}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => unstageChange(facility.facility_unique_id, serviceName, 'start')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        // staged end
        if (inst._stagedEnd && inst.stop_date) {
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        end: {dayjs(inst.stop_date).format("YYYY-MM-DD")}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => unstageChange(facility.facility_unique_id, serviceName, 'end')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        // staged on-hold
        if (inst._stagedHold && inst.on_hold_date) {
            return (
                <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                        on-hold: {dayjs(inst.on_hold_date).format("YYYY-MM-DD")}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => unstageChange(facility.facility_unique_id, serviceName, 'on_hold')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            );
        }

        // committed snapshots (no x)
        if (inst.stop_date) return `end: ${dayjs(inst.stop_date).format("YYYY-MM-DD")}`;
        if (inst.on_hold_date && inst.start_date)
            return `on-hold: ${dayjs(inst.on_hold_date).format("YYYY-MM-DD")}`;
        if (inst.start_date && !inst.stop_date) return "✔";

        return "";
    };

    const handleSaveChanges = async () => {
        const nowISO = new Date().toISOString();
        const batch = { new_services: [], updated_services: [] };

        localSelectedFacilities.forEach((facility) => {
            const facUID = facility.facility_unique_id;
            const facName = facility.facility_name;
            const facId = facility.fac_id ?? facility.facility_id ?? null;
            const mondayItemid = facility.monday_itemid ?? null;

            (facility.services || []).forEach((s) => {
                // new staged row
                if (!s.id && s._stagedNew && s.start_date) {
                    batch.new_services.push({
                        facility_id: facUID,
                        facility_name: facName,
                        fac_id: facId,
                        itemid: mondayItemid,
                        service: {
                            facility_unique_id: facUID,
                            facility_name: facName,
                            fac_id: facId,
                            service_name: s.service_name,
                            start_date: s.start_date,
                            stop_date: s.stop_date || null,
                            on_hold_date: s.on_hold_date || null,
                            rep: s.rep || formData.rep || "",
                            notes: s.notes || { note: [{ date: nowISO, text: formData.notes || "", creator: formData.creator || "" }] },
                            original_creator: s.original_creator || formData.creator || "",
                            created_on: s.created_on || nowISO,
                            updated_on: s.updated_on || nowISO,
                            itemid: mondayItemid,
                        }
                    });
                }

                // staged end/hold updates
                if (s.id && (s._stagedEnd || s._stagedHold)) {
                    batch.updated_services.push({
                        facility_id: facUID,
                        facility_name: facName,
                        fac_id: facId,
                        itemid: mondayItemid,
                        service: {
                            id: s.id,
                            stop_date: s._stagedEnd ? (s.stop_date || null) : (s.stop_date || null),
                            on_hold_date: s._stagedHold ? (s.on_hold_date || null) : (s.on_hold_date || null),
                            notes: s.notes,
                            updated_on: s.updated_on || nowISO,
                            facility_name: facName,
                            fac_id: facId,
                            itemid: mondayItemid,
                        }
                    });
                }

                // staged UNHOLD (convert on-hold → start today)
                if (s.id && s._stagedUnhold) {
                    batch.updated_services.push({
                        facility_id: facUID,
                        facility_name: facName,
                        fac_id: facId,
                        itemid: mondayItemid,
                        service: {
                            id: s.id,
                            start_date: s.start_date,       // <-- send updated start
                            on_hold_date: null,             // <-- clear hold
                            notes: s.notes,
                            updated_on: s.updated_on || nowISO,
                            facility_name: facName,
                            fac_id: facId,
                            itemid: mondayItemid,
                        }
                    });
                }
            });
        });

        if (batch.new_services.length === 0 && batch.updated_services.length === 0) {
            alert("No staged changes to save.");
            return;
        }

        try {
            const url = `${process.env.REACT_APP_API_URL}/facility/batchservices`;

            await axios.post(
                url,
                [batch],
                {
                    headers: {
                        Authorization: `Bearer ${kc.token}`,
                    },
                }
            );

            alert("Changes saved successfully.");
            onUpdateFacilities?.();
            onClose();
        } catch (err) {
            console.error("Failed to save changes:", err);
            alert("Failed to save changes. Please try again.");
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Process Selected Facilities</DialogTitle>
            <DialogContent dividers>
                {localSelectedFacilities.length === 0 ? (
                    <Typography>No facilities selected.</Typography>
                ) : (
                    <>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Facility Name</strong></TableCell>
                                        {serviceOptions.map((sn) => (
                                            <TableCell key={sn}><strong>{sn}</strong></TableCell>
                                        ))}
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {localSelectedFacilities.map((facility) => (
                                        <TableRow key={facility.facility_unique_id}>
                                            <TableCell>{facility.facility_name}</TableCell>
                                            {serviceOptions.map((sn) => (
                                                <TableCell key={sn}>{renderCell(facility, sn)}</TableCell>
                                            ))}
                                            <TableCell align="right">
                                                <IconButton
                                                    onClick={() => handleRemoveFacility(facility.facility_unique_id)}
                                                    sx={{ color: "red" }}
                                                >
                                                    <CloseIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Unified form */}
                        <Box mt={3} p={2} border={1} borderColor="divider" borderRadius={2}>
                            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Service name"
                                    value={formData.service_name}
                                    onChange={(e) => handleChange("service_name", e.target.value)}
                                >
                                    {serviceOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </TextField>

                                <TextField
                                    select
                                    fullWidth
                                    label="Add date"
                                    value={formData.date_type}
                                    onChange={(e) => handleChange("date_type", e.target.value)}
                                >
                                    {DATE_TYPES.map(d => (
                                        <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
                                    ))}
                                </TextField>

                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label={
                                            formData.date_type === "start"
                                                ? "Start date"
                                                : formData.date_type === "end"
                                                    ? "End date"
                                                    : "On-hold date"
                                        }
                                        value={formData.date_value}
                                        onChange={(v) => handleChange("date_value", v)}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                                <TextField
                                    fullWidth
                                    label="Creator"
                                    value={formData.creator}
                                    onChange={(e) => handleChange("creator", e.target.value)}
                                />
                                <TextField
                                    fullWidth
                                    label="Rep"
                                    value={formData.rep}
                                    onChange={(e) => handleChange("rep", e.target.value)}
                                />
                            </Box>

                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Notes"
                                value={formData.notes}
                                onChange={(e) => handleChange("notes", e.target.value)}
                            />

                            <Box mt={2} display="flex" justifyContent="flex-end">
                                <Button variant="outlined" onClick={stageChangeForAll}>
                                    Preview Change (apply to selected)
                                </Button>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">Close</Button>
                <Button onClick={handleSaveChanges} variant="contained" color="primary">
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProcessSelectedModal;
